import React from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { useInstanceMode } from '../../providers/InstanceModeProvider';
import { useLabels } from '../../providers/LabelsProvider';
import { useT } from '../../providers/I18nProvider';

export default function AdminDashboard() {
  const labels = useLabels();
  const t = useT();
  const navigate = useNavigate();
  const { pureOES } = useInstanceMode();

  if (pureOES) return <Navigate to="/dashboard" replace />;
  const role = localStorage.getItem('role');
  const isGlobalAdmin = role === 'global_admin';
  const isOrgAdmin = role === 'admin';
  const isAssessmentAdmin = role === 'assessment_admin';
  const isAnyAdmin = isGlobalAdmin || isOrgAdmin || isAssessmentAdmin;

  const adminCards = [
    {
      title: t('admin.organizationManagement'),
      description: t('admin.orgMgmtDesc'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      path: '/admin/organizations',
      iconColor: 'text-eucnet-sky',
      borderColor: 'border-l-eucnet-sky',
      visible: isGlobalAdmin
    },
    {
      title: t('admin.assessmentManagement'),
      description: t('admin.assessmentMgmtDesc'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      path: '/admin/assessments',
      iconColor: 'text-eucnet-blue',
      borderColor: 'border-l-eucnet-blue',
      visible: isGlobalAdmin || isOrgAdmin || isAssessmentAdmin
    },
    {
      title: t('nav.userManagement'),
      description: t('admin.userMgmtDesc'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      path: '/admin/users',
      iconColor: 'text-eucnet-primary',
      borderColor: 'border-l-eucnet-primary',
      visible: isGlobalAdmin || isOrgAdmin
    },
    {
      title: `${labels.entityName} Management`,
      description: `Manage ${labels.entityNamePluralFull} organizations`,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      path: '/oes',
      iconColor: 'text-eucnet-navy',
      borderColor: 'border-l-eucnet-navy',
      visible: isGlobalAdmin || isOrgAdmin
    },
    {
      title: t('nav.invitations'),
      description: t('admin.invitationsDesc'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      path: '/admin/invitations',
      iconColor: 'text-eucnet-sky',
      borderColor: 'border-l-eucnet-sky',
      visible: isAnyAdmin
    },
    {
      title: t('nav.settings'),
      description: t('admin.settingsDesc'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      path: '/admin/settings',
      iconColor: 'text-eucnet-primary',
      borderColor: 'border-l-eucnet-primary',
      visible: isAnyAdmin
    },
    {
      title: t('nav.security'),
      description: t('admin.securityDesc'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
      path: '/admin/security',
      iconColor: 'text-amber-600',
      borderColor: 'border-l-amber-500',
      visible: isAnyAdmin
    },
    {
      title: t('nav.eventViewer'),
      description: t('admin.eventViewerDesc'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
      path: '/admin/events',
      iconColor: 'text-eucnet-blue',
      borderColor: 'border-l-eucnet-blue',
      visible: isGlobalAdmin || isOrgAdmin
    }
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-6">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{t('nav.administration')}</p>
          <h1 className="text-xl font-bold text-eucnet-navy">
            {isGlobalAdmin ? t('roles.globalAdmin') : isOrgAdmin ? t('roles.organizationAdmin') : isAssessmentAdmin ? t('roles.assessmentAdmin') : t('nav.admin')} {t('admin.controlPanel')}
          </h1>
        </div>

        {/* Admin Cards Grid */}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {adminCards.filter(card => card.visible).map((card, index) => (
            <button
              key={index}
              onClick={() => navigate(card.path)}
              className={`bg-white border border-slate-200 border-l-4 ${card.borderColor} rounded-lg shadow-sm p-5 transition-all duration-200 hover:shadow-md hover:scale-[1.02] text-left`}
            >
              <div className="flex flex-col items-start">
                <div className={`mb-3 p-2.5 rounded-lg bg-slate-50 ${card.iconColor}`}>
                  {card.icon}
                </div>
                <h2 className="text-lg font-semibold text-slate-800 mb-1">{card.title}</h2>
                <p className="text-sm text-slate-500">{card.description}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Quick Info Section */}
        <div className="mt-6 bg-white rounded-lg border border-slate-200 shadow-sm p-5">
          <h2 className="text-lg font-semibold text-eucnet-navy mb-3">{t('admin.quickInfo')}</h2>
          <div className="flex items-start gap-2.5 text-slate-500">
            <svg className="w-4 h-4 mt-0.5 text-eucnet-sky flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p className="text-sm">
              {isGlobalAdmin
                ? t('admin.globalAdminInfo')
                : isOrgAdmin
                ? t('admin.orgAdminInfo', { entity: labels.entityNamePlural })
                : isAssessmentAdmin
                ? t('admin.assessmentAdminInfo', { entity: labels.entityNamePlural })
                : t('admin.adminControlPanel')}
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
